import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { editAdminDamageReportAction, getAdminDamageReportAction } from "../../store/actions";
import {
  Box, Typography, Button, Chip, Container, Card, CardContent, Divider, Dialog,
  DialogTitle, DialogContent, DialogActions, IconButton, ImageList, ImageListItem, CircularProgress
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ManufacturingImg from "../../assets/ManufacturingImg.png";
import AutomateImg from "../../assets/AutomateImg.png";

const getStatusColor = (status) => {
  switch (status) {
    case "pending":
      return {
        backgroundColor: "#FEF3C7",
        color: "#92400E"
      };
    case "accepted":
      return {
        backgroundColor: "#D1FAE5",
        color: "#065F46"
      };
    default:
      return {
        backgroundColor: "#DBEAFE",
        color: "#1E40AF"
      };
  }
};

const DamageShortage = () => {
  const dispatch = useDispatch();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [openImageDialog, setOpenImageDialog] = useState(false);

  // Get damage reports from Redux store
  const { successData: damageReports, loading, error } = useSelector((state) => state.getAdminDamageReportReducer);
  const { successData: editSuccess } = useSelector((state) => state.editAdminDamageReportReducer);

  // Dispatch the action when component mounts
  useEffect(() => {
    dispatch(getAdminDamageReportAction());
  }, [dispatch, editSuccess]);

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleApprove = () => {
    // Logic to approve the report
    console.log("Approved report:", selectedReport);
    dispatch(editAdminDamageReportAction({
      endPoint: `admin/damageManagement/${selectedReport.id}`,
      status: "accepted",
    }))
    setOpenDialog(false);
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setOpenImageDialog(true);
  };

  const handleCloseImageDialog = () => {
    setOpenImageDialog(false);
  };

  // Determine which reports to display
  const displayReports = damageReports?.data;

  return (
    <Box sx={{ bgcolor: "#F9FAFB", minHeight: "100vh", mt: { xs: '15%', sm: '7%' }, p: 3, width: { xs: '90%', sm: '75%' }, ml: { xs: '0%', sm: '10%',md:"20%" } }}>
      <Container maxWidth="xl">
        <Box
          sx={{
            p: 2,
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
           <Box>
            <h1 style={{ fontWeight: "bold", fontSize: "2rem", fontFamily: "Poppins" }}>
              Damage and Shortage Reports
            </h1>
          </Box>
          {/* <Button
            variant="contained"
            startIcon={<AddCircleOutlinedIcon sx={{ p: 1, bgcolor: '#E7EDFF', borderRadius: '50%', color: '#3C1ED5' }} />}
            sx={{
              backgroundColor: '#CBD6F9',
              color: '#232323',
              fontFamily: 'Poppins',
              borderRadius: '10px',
              padding: '20px',
            }}
          >
            Add New Report
          </Button> */}
        </Box>

        <Box sx={{ mt: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ p: 3, bgcolor: '#FEE2E2', borderRadius: 2, color: '#B91C1C' }}>
              <Typography>Error loading damage reports: {error}</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {displayReports?.length > 0 ? (
                displayReports?.map((report, index) => (
                  <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={report.reportId || `report-${index}`}>
                    <Card sx={{
                      height: '100%',
                      border: "1px solid #F3F4F6",
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                      transition: 'transform 0.2s',
                    }}>
                      <CardContent>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                          <Box>
                            <Typography variant="h6" fontWeight="medium">
                              {report.RawMaterial.name}
                            </Typography>
                          </Box>
                          <Chip
                            label={report.status}
                            sx={{
                              ...getStatusColor(report.status),
                              fontWeight: "medium"
                            }}
                          />
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            ReasonCreatedBy
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {report.User.full_name}
                          </Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Quantity Damaged
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {report.quantity_damaged}
                          </Typography>
                        </Box>

                        <Button
                          variant="contained"
                          fullWidth
                          sx={{
                            mt: 2,
                            backgroundColor: '#3C1ED5',
                            '&:hover': {
                              backgroundColor: '#2c16a0',
                            }
                          }}
                          onClick={() => handleViewReport(report)}
                        >
                          View Details
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#F9FAFB', borderRadius: 2 }}>
                    <Typography variant="h6" color="text.secondary">
                      No damage reports found
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </Box>
      </Container>

      {/* Detail Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#f8f9fa',
          borderBottom: '1px solid #e9ecef'
        }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
            Damage Report Details
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {selectedReport && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item size={{ xs: 12 }}>
                  <Box >
                    <Typography variant="body2" color="text.secondary" >
                      Name
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedReport.RawMaterial.name}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item size={{ xs: 12 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" >
                      Reason Created By
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedReport.User.full_name}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item size={{ xs: 12 }}>
                  <Box >
                    <Typography variant="body2" color="text.secondary" >
                      Quantity Damaged
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedReport.quantity_damaged}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item size={{ xs: 12 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" >
                      Reason
                    </Typography>
                    <Typography variant="body1" sx={{ p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1, }}>
                      {selectedReport.reason}
                    </Typography>
                  </Box>
                </Grid>

                {/* Images Section */}
                <Grid item size={{ xs: 12 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" >
                      Damage Images
                    </Typography>
                    {selectedReport.images && selectedReport.images.length > 0 ? (
                      <ImageList sx={{ width: '100%', height: 200 }} cols={3} rowHeight={164}>
                        {selectedReport.images.map((img, index) => (
                          <ImageListItem
                            key={index}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.8 }
                            }}
                            onClick={() => handleImageClick(img)}
                          >
                            <img
                              src={img}
                              alt={`Damage evidence ${index + 1}`}
                              loading="lazy"
                              style={{ borderRadius: 4 }}
                            />
                          </ImageListItem>
                        ))}
                      </ImageList>
                    ) : (
                      <Typography variant="body1" sx={{ p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                        No images available
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>

          <DialogActions sx={{ p: 3, borderTop: '1px solid #e9ecef' }}>
            <Button
              onClick={handleCloseDialog}
              variant="outlined"
              sx={{ mr: 1 }}
            >
              Close
            </Button>

            {selectedReport && selectedReport.status === "pending" ? (
              <Button
                onClick={handleApprove}
                variant="contained"
                color="success"
                startIcon={<CheckCircleOutlineIcon />}
                sx={{
                  bgcolor: '#10B981',
                  '&:hover': {
                    bgcolor: '#059669',
                  }
                }}
              >
                Approve Report
              </Button>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleOutlineIcon sx={{ color: '#10B981', mr: 1 }} />
                <Typography sx={{ color: '#10B981', fontWeight: 'bold' }}>
                  Approved
                </Typography>
              </Box>
            )}
          </DialogActions>

      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={openImageDialog}
        onClose={handleCloseImageDialog}
        maxWidth="md"
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          p: 1
        }}>
          <IconButton onClick={handleCloseImageDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Damage evidence"
              style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DamageShortage;
